import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import {
  addressHasStreetNumber,
  hasAzureMapsKey,
  suggestAddresses
} from '../services/geocodeService';

/**
 * Norwegian address picker backed by Azure Maps.
 * Parent should only persist when onChange reports confirmed=true.
 * After a numbered address is selected, apartment/unit text may be appended
 * (e.g. " … H0102") without clearing confirmation.
 */
export default function AddressAutocomplete({
  label = 'Adresse',
  value = '',
  onChange,
  required = false,
  disabled = false
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const selectedBaseRef = useRef('');
  const requestIdRef = useRef(0);
  const mapsConfigured = useMemo(() => hasAzureMapsKey(), []);

  useEffect(() => {
    setInputValue(value || '');
    if (value) {
      setSelected({ formattedAddress: value, lat: null, lng: null, hasStreetNumber: addressHasStreetNumber(value) });
      selectedBaseRef.current = value;
    } else {
      setSelected(null);
      selectedBaseRef.current = '';
    }
  }, [value]);

  useEffect(() => {
    if (!mapsConfigured) {
      setOptions([]);
      return undefined;
    }

    const q = inputValue.trim();
    if (q.length < 3) {
      setOptions([]);
      return undefined;
    }

    // Don't refetch while user only appends apartment text after a confirmed base
    const base = selectedBaseRef.current;
    if (base && q.toLowerCase().startsWith(base.toLowerCase()) && q.length > base.length) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const suggestions = await suggestAddresses(q);
        if (requestId === requestIdRef.current) {
          setOptions(suggestions);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, mapsConfigured]);

  const helperText = !mapsConfigured
    ? 'Adresseoppslag er ikke konfigurert (REACT_APP_AZURE_MAPS_KEY mangler).'
    : 'Skriv gatenavn og husnummer (f.eks. Storgata 12), velg fra listen. Leilighet/boenhet kan skrives inn etterpå (f.eks. H0102).';

  const emitConfirmed = (address, base, lat, lng) => {
    onChange?.({
      address,
      confirmed: addressHasStreetNumber(base),
      lat,
      lng
    });
  };

  return (
    <Autocomplete
      fullWidth
      freeSolo={false}
      disabled={disabled || !mapsConfigured}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option?.formattedAddress || ''
      }
      isOptionEqualToValue={(a, b) =>
        (a?.formattedAddress || a) === (b?.formattedAddress || b)
      }
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        setInputValue(next);
        if (reason !== 'input') {
          return;
        }

        const base = selectedBaseRef.current;
        const trimmedNext = next.trim();
        const inheritsSelection =
          Boolean(base) &&
          trimmedNext.toLowerCase().startsWith(base.toLowerCase()) &&
          addressHasStreetNumber(base);

        if (inheritsSelection) {
          // Keep geocode confirmation; allow apartment/unit as free-text suffix
          setSelected((prev) =>
            prev
              ? { ...prev, formattedAddress: next }
              : { formattedAddress: next, lat: null, lng: null, hasStreetNumber: true }
          );
          emitConfirmed(next, base, selected?.lat, selected?.lng);
          return;
        }

        selectedBaseRef.current = '';
        setSelected(null);
        onChange?.({ address: next, confirmed: false });
      }}
      onChange={(_, next) => {
        if (!next || typeof next === 'string') {
          selectedBaseRef.current = '';
          setSelected(null);
          onChange?.({ address: typeof next === 'string' ? next : '', confirmed: false });
          return;
        }

        if (!next.hasStreetNumber && !addressHasStreetNumber(next.formattedAddress)) {
          // Street-only hit — keep searching; do not treat as saveable
          selectedBaseRef.current = '';
          setSelected(null);
          setInputValue(next.formattedAddress);
          onChange?.({ address: next.formattedAddress, confirmed: false });
          return;
        }

        selectedBaseRef.current = next.formattedAddress;
        setSelected(next);
        setInputValue(next.formattedAddress);
        emitConfirmed(next.formattedAddress, next.formattedAddress, next.lat, next.lng);
      }}
      renderOption={(props, option) => (
        <li {...props} key={`${option.formattedAddress}-${option.lat}-${option.lng}`}>
          {option.formattedAddress}
          {!option.hasStreetNumber ? ' (gate — legg til husnummer)' : ''}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(required && !selected?.formattedAddress && mapsConfigured)}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
}
